import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect
} from 'aws-amplify/auth';
import { authApi } from '@/services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      setLoading(true);
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      if (currentUser && session.tokens) {
        // Extract ID token for reading claims (email, name, etc.).
        // Token is NOT stored — Amplify manages token lifecycle internally.
        // API calls retrieve fresh tokens via fetchAuthSession() in the Axios interceptor.
        const idToken = session.tokens.idToken;

        // Extract email from ID token (works for both native and Google federated users).
        // For Google users, currentUser.username is "Google_<id>", not their email.
        const email = idToken?.payload?.email
          || currentUser.signInDetails?.loginId
          || currentUser.username;

        // Try to get user profile from backend
        try {
          const response = await authApi.getUser();
          setUser(response.data.data);
        } catch {
          // Backend might not have user yet, use token info
          setUser({
            email,
            cognito_sub: currentUser.userId,
            name: idToken?.payload?.name || null,
          });
        }
      }
    } catch (err) {
      // Not authenticated
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Sign in with email and password
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      const result = await signIn({ username: email, password });

      if (result.isSignedIn) {
        await checkAuthState();
        return { success: true };
      }

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        return { success: false, needsConfirmation: true, email };
      }

      return { success: false, nextStep: result.nextStep };
    } catch (err) {
      const message = err.message || 'Sign in failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Sign up with email and password
  const signup = useCallback(async (email, password, name) => {
    try {
      setError(null);
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
          },
        },
      });

      if (result.isSignUpComplete) {
        return { success: true, complete: true };
      }

      return {
        success: true,
        complete: false,
        nextStep: result.nextStep,
        codeDeliveryDetails: result.nextStep?.codeDeliveryDetails
      };
    } catch (err) {
      const message = err.message || 'Sign up failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Confirm sign up with verification code
  const confirmSignup = useCallback(async (email, code) => {
    try {
      setError(null);
      await confirmSignUp({ username: email, confirmationCode: code });
      return { success: true };
    } catch (err) {
      const message = err.message || 'Verification failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Resend confirmation code
  const resendCode = useCallback(async (email) => {
    try {
      setError(null);
      await resendSignUpCode({ username: email });
      return { success: true };
    } catch (err) {
      const message = err.message || 'Failed to resend code';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Forgot password - send reset code
  const forgotPassword = useCallback(async (email) => {
    try {
      setError(null);
      const result = await resetPassword({ username: email });
      return {
        success: true,
        codeDeliveryDetails: result.nextStep?.codeDeliveryDetails
      };
    } catch (err) {
      const message = err.message || 'Failed to send reset code';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Confirm forgot password with code and new password
  const confirmForgotPassword = useCallback(async (email, code, newPassword) => {
    try {
      setError(null);
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
      return { success: true };
    } catch (err) {
      const message = err.message || 'Password reset failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Sign in with Google
  const loginWithGoogle = useCallback(async () => {
    try {
      setError(null);
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      const message = err.message || 'Google sign in failed';
      setError(message);
      throw new Error(message);
    }
  }, []);

  // Logout — clears user state and app context from localStorage
  const logout = useCallback(async () => {
    try {
      await signOut({ global: true });
    } catch (err) {
      // Ignore logout errors
    } finally {
      setUser(null);
      localStorage.removeItem('currentAppId');
    }
  }, []);

  // Refresh user data from backend
  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.getUser();
      setUser(response.data.data);
      return response.data.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to refresh user data');
      throw err;
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isAdmin,
    login,
    signup,
    confirmSignup,
    resendCode,
    forgotPassword,
    confirmForgotPassword,
    loginWithGoogle,
    logout,
    refreshUser,
    setUser,
    clearError,
    checkAuthState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
